import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import {
  IMembership,
  useMembershipsQuery,
  useUpdateCompanyMembershipMutation
} from '../../../../generated/graphql';
import { Colors } from '../../../../utils/theme';
import EditButton from '../../../Common/Buttons/EditButton';
import moment from 'moment-timezone';
import AutocompleteComponent from '../../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../../Common/Fields/InputComponent';
import Tag from '../../../../assets/images/icons/hashtag.svg';
import line_style from '../../../../assets/images/icons/line_style.svg';
import { Today } from '@mui/icons-material';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';

interface IErrors {
  membershipName?: string | null;
}

const defaultErrorsObj: IErrors = {
  membershipName: null
};

interface IProps {
  membership: IMembership;
  companyName: string | null | undefined;
  companyId: string;
  updateMembershipProperty: (mem: IMembership) => void;
}

const CompanyMembershipTab: React.FunctionComponent<IProps> = ({
  membership,
  companyName,
  companyId,
  updateMembershipProperty
}: IProps) => {
  const [editable, setEditable] = useState(false);
  const getMemberships = useMembershipsQuery();
  const { dispatch } = useContext(AppContext);
  const [formErrors, setFormErroes] = useState(defaultErrorsObj);
  const [membershipNameOptions, setMembershipNameOptions] = useState([]);
  const [membershipName, setMembershipName] = useState<IMembership | null>(membership);
  const [updateMembershipName, updateMembershipNameResult] = useUpdateCompanyMembershipMutation();

  useEffect(() => {
    if (getMemberships.data && getMemberships.data.result) {
      setMembershipNameOptions(getMemberships.data.result as never[]);
    }
  }, [getMemberships.data]);

  const getMembershipStartData = () => {
    return membershipName?.start_date
      ? moment(parseInt(membershipName.start_date)).format('MMM D, YYYY')
      : '';
  };

  const getMembershipEndData = () => {
    return membershipName?.end_date
      ? moment(parseInt(membershipName.end_date)).format('MMM D, YYYY')
      : '';
  };

  const handleEditClicked = () => {
    setEditable(true);
  };

  const handleMembershipChanged = (
    _: unknown,
    newValue: React.SetStateAction<IMembership | null>
  ) => {
    if (newValue) {
      setFormErroes({ ...formErrors, membershipName: null });
    } else {
      setFormErroes({ ...formErrors, membershipName: 'Membership is required' });
    }
    setMembershipName(newValue);
  };

  const handleCancel = () => {
    setMembershipName(membership);
    setFormErroes(defaultErrorsObj);
    setEditable(false);
  };

  const handleSave = () => {
    const errors = {
      membershipName: null
    } as IErrors;
    let hasErrors = false;
    if (membershipName === null) {
      hasErrors = true;
      errors.membershipName = 'Membership is required!';
    }
    if (hasErrors) {
      setFormErroes({ ...errors });
    } else {
      if (membershipName?.id) {
        updateMembershipName({
          variables: {
            id: parseInt(companyId),
            membershipId:
              membershipName?.id !== null ? parseInt(membershipName?.id) : parseInt(membership.id)
          },
          update(_, { data }) {
            if (data?.result) {
              updateMembershipProperty(data.result.membership as IMembership);
              setEditable(false);
              dispatch({
                type: Actions.ShowMessage,
                payload: {
                  text: `Successfylly updated ${data.result.name}`,
                  severity: 'success'
                }
              });
            }
          }
        }).catch((e) => {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'error',
              text: e.message,
              autoHide: 6000
            }
          });
        });
      } else {
        setEditable(false);
      }
    }
  };

  return (
    <Box sx={{ padding: '80px', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="company-membership-edit-button"
          onClick={handleEditClicked}
          disabled={editable}
          editButtonColor={editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '50px 6.5%',
          paddingX: '23.5%',
          pt: '80px'
        }}
      >
        <AutocompleteComponent
          id="membership-name"
          label="Membership Name"
          options={membershipNameOptions}
          getOptionLabel={(option: IMembership) => option.name}
          disabled={!editable}
          values={membershipName}
          onChange={handleMembershipChanged}
          errorMsg={formErrors.membershipName}
          additionalStyle={{ gridColumn: 'span 2' }}
        />
        <InputComponent
          id="membership-id"
          label="Membership ID"
          value={membershipName !== null ? membershipName?.id : ''}
          disabled
          startAdornment={<img src={Tag} alt="hashtagButton" style={{ marginRight: '12px' }} />}
        />
        <InputComponent
          id="company-name"
          label="Company Name"
          value={companyName ?? ''}
          disabled
          startAdornment={
            <img src={line_style} alt="lineStyleButton" style={{ marginRight: '12px' }} />
          }
        />
        <InputComponent
          id="membership-start-date"
          label="Membership Start Date"
          value={getMembershipStartData()}
          disabled
          startAdornment={
            <Today
              style={{
                marginRight: '12px',
                color: Colors.gray
              }}
            />
          }
        />
        <InputComponent
          id="membership-end-date"
          label="Membership End Date"
          value={getMembershipEndData()}
          disabled
          startAdornment={
            <Today
              style={{
                marginRight: '12px',
                color: Colors.gray
              }}
            />
          }
        />
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '100px' }}>
          <CancelButton
            id="company-membership-cancel-button"
            disabled={updateMembershipNameResult.loading}
            onClick={handleCancel}
            text="Cancel"
          />
          <SaveButton
            id="company-membership-save-button"
            disabled={updateMembershipNameResult.loading}
            onClick={handleSave}
            text="Save"
          />
        </Box>
      )}
    </Box>
  );
};

export default CompanyMembershipTab;
